/**
 * Cookie consent storage helper.
 *
 * Values stored in localStorage under key "sunify_consent_v1":
 *   {
 *     version: 1,
 *     timestamp: ISO string,
 *     necessary: true,       // always granted, cannot be refused
 *     analytics: boolean,    // Google Analytics, Hotjar, etc.
 *     marketing: boolean,    // Meta Pixel, remarketing
 *   }
 *
 * We deliberately NOT use cookies for the consent record itself — localStorage
 * is enough and avoids chicken-and-egg issues.
 */

const KEY = 'sunify_consent_v1';

export const CONSENT_CATEGORIES = ['necessary', 'analytics', 'marketing'];

export const defaultConsent = () => ({
  version: 1,
  timestamp: null,
  necessary: true,
  analytics: false,
  marketing: false,
});

export const getStoredConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1) return null;
    return { ...defaultConsent(), ...parsed };
  } catch {
    return null;
  }
};

export const setStoredConsent = (consent) => {
  if (typeof window === 'undefined') return;
  const merged = {
    ...defaultConsent(),
    ...consent,
    version: 1,
    necessary: true,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(KEY, JSON.stringify(merged));
  // Let listeners (e.g. GA loader) react without a page reload
  window.dispatchEvent(new CustomEvent('sunify:consent-changed', { detail: merged }));
  pushConsentToGtag(merged);
  return merged;
};

export const hasConsentDecision = () => {
  const c = getStoredConsent();
  return !!(c && c.timestamp);
};

/**
 * Google Consent Mode v2 — if gtag is loaded (e.g. via GTM), propagate our
 * consent state. Safe to call even before any tag is on the page, because
 * gtag queues the call in the dataLayer.
 */
export const pushConsentToGtag = (consent) => {
  if (typeof window === 'undefined') return;
  const gtag = window.gtag;
  const dataLayer = window.dataLayer;
  const payload = {
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    functionality_storage: 'granted',
    personalization_storage: consent.analytics ? 'granted' : 'denied',
    security_storage: 'granted',
  };
  if (typeof gtag === 'function') {
    gtag('consent', 'update', payload);
  } else if (Array.isArray(dataLayer)) {
    dataLayer.push(['consent', 'update', payload]);
  }
};
